import { useState, useEffect } from 'react';
import { ArrowLeft, PanelLeftOpen, Download, Map as MapIcon, MessageSquare, Sun, Moon, Check, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface Props {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  activeMode: 'sitemap' | 'comment' | 'design-system';
  onChangeMode: (mode: 'sitemap' | 'comment' | 'design-system') => void;
  isGlobalDarkMode: boolean;
  setIsGlobalDarkMode: (dark: boolean) => void;
  isSaving?: boolean;
  lastSaved?: Date | null;
  backPath?: string;
  blueprintName?: string;
  hideSidebarToggle?: boolean;
  onSave?: () => void;
}

export function FloatingHeader({ sidebarOpen, onToggleSidebar, activeMode, onChangeMode, isGlobalDarkMode, setIsGlobalDarkMode, isSaving, lastSaved, backPath = '/brand/sitemaps', blueprintName, hideSidebarToggle, onSave }: Props) {
  const navigate = useNavigate();
  const [saveStatusText, setSaveStatusText] = useState('Saved');

  useEffect(() => {
    if (isSaving) {
      setSaveStatusText('Saving…');
      return;
    }

    if (!lastSaved) {
      setSaveStatusText('Saved');
      return;
    }

    const updateText = () => {
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - lastSaved.getTime()) / 1000);

      if (diffInSeconds < 5) {
        setSaveStatusText('Saved just now');
      } else if (diffInSeconds < 60) {
        setSaveStatusText(`Saved ${diffInSeconds}s ago`);
      } else {
        const diffInMinutes = Math.floor(diffInSeconds / 60);
        setSaveStatusText(`Saved ${diffInMinutes}m ago`);
      }
    };

    updateText();
    const interval = setInterval(updateText, 30000); // Update every 30s
    return () => clearInterval(interval);
  }, [isSaving, lastSaved]);

  return (
    <div className="flex items-center justify-between w-full">
      {/* Left section - only show when sidebar is collapsed */}
      {!sidebarOpen && (
        <div className="flex items-center gap-1 bg-card/95 backdrop-blur-sm rounded-xl border border-border shadow-lg px-2 py-1.5">
          <button
            onClick={() => navigate(backPath)}
            className="p-2 rounded-lg text-muted-foreground hover:bg-secondary hover:text-secondary-foreground transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft size={18} />
          </button>
          {blueprintName && (
            <>
              <div className="w-px h-5 bg-border mx-0.5" />
              <span className="text-sm font-semibold text-foreground px-2 truncate max-w-[160px] tracking-tight">
                {blueprintName}
              </span>
            </>
          )}
          {!hideSidebarToggle && (
            <>
              <div className="w-px h-5 bg-border mx-0.5" />
              <button
                onClick={onToggleSidebar}
                className="p-2 rounded-lg text-muted-foreground hover:bg-secondary hover:text-secondary-foreground transition-colors"
                title="Open sidebar"
              >
                <PanelLeftOpen size={18} />
              </button>
            </>
          )}
        </div>
      )}
      {sidebarOpen && <div />}

      {/* Center section - Mode tabs */}
      <div className="relative flex items-center bg-muted/90 backdrop-blur-sm rounded-xl border border-border shadow-lg p-1">
        <button
          onClick={() => onChangeMode('sitemap')}
          className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
            activeMode === 'sitemap'
              ? 'text-secondary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {activeMode === 'sitemap' && (
            <motion.div
              layoutId="tab-indicator"
              className="absolute inset-0 rounded-lg bg-background shadow-sm ring-1 ring-black/5 dark:ring-white/10"
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
          )}
          <span className="relative z-10 flex items-center gap-2">
            <MapIcon size={16} />
            Sitemap
          </span>
        </button>
        <button
          onClick={() => onChangeMode('design-system')}
          className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
            activeMode === 'design-system'
              ? 'text-secondary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {activeMode === 'design-system' && (
            <motion.div
              layoutId="tab-indicator"
              className="absolute inset-0 rounded-lg bg-background shadow-sm ring-1 ring-black/5 dark:ring-white/10"
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
          )}
          <span className="relative z-10 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-palette"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>
            Design System
          </span>
        </button>
      </div>

      {/* Right side container */}
      <div className="flex items-center gap-2 bg-card/95 backdrop-blur-sm rounded-xl border border-border shadow-lg px-3 py-1.5">
        
        {/* Comments Toggle - Only in Sitemap mode */}
        {activeMode !== 'design-system' && (
          <button
            onClick={() => onChangeMode(activeMode === 'comment' ? 'sitemap' : 'comment')}
            className={`p-1.5 rounded-md transition-colors mr-1 ${
              activeMode === 'comment'
                ? 'bg-workspace-accent text-white'
                : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
            title="Toggle Comments"
          >
            <MessageSquare size={16} />
          </button>
        )}

        {/* Theme toggle */}
        <button
          onClick={() => setIsGlobalDarkMode(!isGlobalDarkMode)}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors mr-1"
          title={isGlobalDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          {isGlobalDarkMode ? <Moon size={16} /> : <Sun size={16} />}
        </button>
        
        {/* Unified Save Button */}
        <div className="flex items-center gap-2">
          {lastSaved && !isSaving && (
            <span className="text-xs text-muted-foreground mr-1">
              {saveStatusText}
            </span>
          )}
          <button
            onClick={() => {
              if (activeMode === 'design-system') {
                window.dispatchEvent(new CustomEvent('design-studio-save'));
              } else if (onSave) {
                onSave();
              }
            }}
            disabled={isSaving}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              !isSaving && lastSaved
                ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/50'
                : 'bg-workspace-accent text-white hover:bg-workspace-accent/90'
            }`}
          >
            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
        
        {activeMode === 'design-system' ? (
          <button
            onClick={() => {
              const event = new CustomEvent('design-studio-export-css');
              window.dispatchEvent(event);
            }}
            className={`px-4 py-1.5 ml-1 rounded-md text-sm font-medium transition-colors ${
              isGlobalDarkMode
                ? 'bg-zinc-100 text-zinc-950 hover:bg-zinc-200'
                : 'bg-zinc-900 text-zinc-50 hover:bg-zinc-800'
            }`}
            title="Export CSS"
          >
            Export
          </button>
        ) : (
          <button
            className={`px-4 py-1.5 ml-1 rounded-md text-sm font-medium transition-colors ${
              isGlobalDarkMode
                ? 'bg-zinc-100 text-zinc-950 hover:bg-zinc-200'
                : 'bg-zinc-900 text-zinc-50 hover:bg-zinc-800'
            }`}
            title="Export"
          >
            Export
          </button>
        )}
      </div>
    </div>
  );
}
